// WHICH PANE A PERMISSION SUBSECTION GOVERNS — one map, one copy.
//
// THE COMPLAINT THIS ANSWERS. `TeamAccessPanel` grouped a case's rights under
// headings like "Identity & description" and never said WHICH SCREEN those
// rights govern. A responsible partner deciding who may edit what could not tell
// from the panel which pane of the case file was at stake, and the answer was in
// a tooltip that repeated the row and column headers.
//
// ── IT IS A POINTER, NEVER A NAME ───────────────────────────────────────────
//
// Every value below is an i18n KEY the application already renders as a heading
// somewhere — `case.info` is the heading of the case-info card, `case.tasks` is
// the Tasks pane's own. Nothing here is a string typed for this panel, and that
// is the whole design: `server/src/lib/translationOverlay.js` merges a tenant's
// own `TranslationOverride` rows over the global dictionary, so **a vertical
// that calls its matters projects renames the heading once and this panel
// follows**. A gallery reads "Project info" here because it reads "Project info"
// on the pane itself. A literal would have to be renamed twice and would drift
// the first time somebody forgot.
//
// ── WHERE IT LIVES, AND WHY NOT IN THE API ──────────────────────────────────
//
// `server/src/lib/sectionRegistry.js` requires this file and re-exports it, so
// the map sits beside SUBSECTIONS for every server-side reader — the carve-out
// included — while `src/components/TeamAccessPanel.tsx` imports it directly.
// **One copy, two readers, no round trip.** It is deliberately NOT added to
// `GET /api/permissions/catalog`: nothing server-side consumes it today, and an
// unused API field would cost a re-record of the API baseline for nothing
// (register 83v carries that measurement). The day a server-side reader needs
// it, it is one line in `CATALOG_SECTIONS` and one deliberate re-record.
//
// The `.mjs`-in-`src/lib` shape is this repository's established way of sharing
// a pure module across the two planes — `cddIdentity.mjs`, `mentionTokens.mjs`,
// `caseRefPattern.mjs`, `coiMatch.mjs`, `htmlEscape.mjs`, `clientMatters.mjs`
// all do it, each imported by the client and `require`d by the server.
//
// ── THE TWO WITH NO PANE ARE THE POINT, NOT THE GAP ─────────────────────────
//
// The recon that preceded this map said FIVE, and it was wrong by three.
// `cv.identity`, `cv.terms` and `cv.parties` do have headings — `case.info` and
// `case.parties` — they simply are not `csSec-*` panes with a `SectionHeader`,
// which is what that pass had searched for. **A census that looks for one
// construction reports the absence of the construction, not the absence of the
// thing.** Corrected by writing the entries out one at a time.
//
// TWO subsections govern rights the case view does not render: `cv.chat` and
// `cv.proformas`. Saying so is more useful than inventing a pane for them, and
// **it is the carve-out's first data point** — these are exactly the rights
// whose surface lives outside the view that owns them, which is a boundary that
// has to be drawn anyway.
//
//   `elsewhere` names the surface, again by an i18n key the app already renders.
//   `why` is written only where NO key exists, and is a fact about this
//   repository rather than a label — it is not rendered to anybody.
//
// ── CHECKED, NOT REMEMBERED ─────────────────────────────────────────────────
//
// `scripts/lint/subsection-panes.js` fails CI when a case-internal subsection
// has no entry here, and when a key named here has no en/fr/nl row in the seed
// the server actually serves. Unchecked, this rots exactly the way the
// coordinate citations did — and this week has already spent four findings on
// instruments nobody checked.

/**
 * subsection id → what it governs.
 *
 *   { pane }              a pane IN this view, named by the key it renders
 *   { elsewhere }         a surface OUTSIDE this view, named the same way
 *   { why }               no client surface consults these rights at all
 */
export const SUBSECTION_PANE = {
  // ── CaseView ──────────────────────────────────────────────────────────────
  // Identity, terms and the financial summary are three blocks of ONE card, so
  // three subsections point at one heading. That is the map being honest about
  // the app rather than the app being tidy.
  'cv.identity': { pane: 'case.info' },
  'cv.terms': { pane: 'case.info' },
  'cv.parties': { pane: 'case.parties' },
  'cv.financials': { pane: 'case.financial_summary' },
  'cv.timeline': { pane: 'case.timeline' },
  // 182 A.2 — the Notes section's own heading (the contact timeline).
  'cv.notes': { pane: 'caseView.notes.title' },
  'cv.tasks': { pane: 'case.tasks' },
  // `cv.time` covers time entries, the time log and the billed flag; the pane is
  // named for the first. The subsection is wider than its pane and the map says
  // which pane, not which key.
  'cv.time': { pane: 'case.time_entries' },
  // `case.documents` reaches the heading as a `titleKey` PROP on
  // CaseDocumentsSection rather than a `t("…")` in the view — which is why the
  // lint matches the key as a quoted string anywhere in src/ and says so.
  'cv.documents': { pane: 'case.documents' },
  // 194 B.0 — the two panes of the Communication section, by their own headings.
  'cv.emails': { pane: 'communication.pane_emails' },
  'cv.whatsapp': { pane: 'communication.pane_messages' },
  'cv.agenda': { pane: 'case.agenda' },
  'cv.cdd': { pane: 'case.section.cdd' },
  'cv.calculator': { pane: 'case.calculator.title' },

  // ── CollectiveCaseView ────────────────────────────────────────────────────
  'ccv.clients': { pane: 'case.parties.clients' },
  'ccv.financials': { pane: 'case.retainer_rounds' },
  // The bulk mailer and the contact-list builder are MODALS, opened from inside
  // the Clients pane (CollectiveCaseView mounts both within `csSec-ccClients`),
  // so this points at the pane a person acts on rather than at a heading no
  // screen renders. `cc.bulkMailer.title` was the first guess and the lint
  // refused it: no such key exists.
  'ccv.mailing': {
    pane: 'case.parties.clients',
    why: 'the bulk mailer and the contact-list builder are modals opened from that pane, not panes of their own',
  },

  // ── THE ONES WITH NO PANE IN THE VIEW THAT OWNS THEM ─────────────────────
  //
  // `caseView.chat.*` is keyed to a case and read by the SEARCH CHIP and the
  // chat surface (`sectionMeta.ts` SURFACE_SECTION_KEY: `chat →
  // caseView.chat.view`). The case view renders no chat pane; the conversation
  // opens from the sidebar and the pop-out.
  'cv.chat': { elsewhere: 'nav.general_chat' },
  // `caseView.proformas.*` has NO reader anywhere in `src/` — measured, not
  // assumed. Both rights are enforced server-side on the invoicing routes, and
  // the surface a person would look for is the Invoices view.
  'cv.proformas': {
    elsewhere: 'nav.invoices',
    why: 'no component in src/ consults caseView.proformas.* — the rights are enforced server-side only',
  },
};

/** The one new string this map needs: how a surface outside the view is marked. */
export const NOT_IN_VIEW_KEY = 'permPane.not_in_view';

/**
 * What to draw beside a subsection heading, or null when the map says nothing.
 *
 * `t` is the caller's translator, so this stays a pure function of the map and
 * the dictionary — no import of the i18n runtime, which is what lets the server
 * require the same file.
 */
export function paneLabel(subId, t) {
  const e = SUBSECTION_PANE[subId];
  if (!e) return null;
  if (e.pane) return { text: t(e.pane), here: true };
  if (e.elsewhere) return { text: t(e.elsewhere), here: false };
  return null;
}

/** Every i18n key this map points at — what the lint has to resolve. */
export function panePointerKeys() {
  const keys = new Set([NOT_IN_VIEW_KEY]);
  for (const e of Object.values(SUBSECTION_PANE)) {
    if (e.pane) keys.add(e.pane);
    if (e.elsewhere) keys.add(e.elsewhere);
  }
  return [...keys].sort();
}
